import React, { useEffect, useMemo, useRef, useState } from "react";
import { Row, Col, CardBody, Card, Button, Input } from "reactstrap";
import Breadcrumb from "components/Common/Breadcrumb";
import { GoogleMap, MarkerF, InfoWindow, useJsApiLoader } from "@react-google-maps/api";
import { AdminService } from "services/AdminService";
import { toast } from "react-toastify";
import { initializeApp } from "firebase/app";
import { getDatabase, ref, onValue, off } from "firebase/database";
import { environment } from "environment/environment";
import { FaUserTie, FaBox, FaSearch, FaRoute, FaMapMarkerAlt, FaUserSlash, FaUsers, FaFilter } from "react-icons/fa";
import { FiChevronDown, FiChevronUp, FiDownload, FiX } from "react-icons/fi";
import { Order } from "models/Order";
import AssignDriver from "admin/dispatch/AssignDriver";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { OrderStatus } from "enum/OrderStatus";
import { DispatchType } from "enum/DispatchType";
import UpdateStatus from "admin/dispatch/UpdateStatus";
import { OrderRequest } from "models/Admin";
// import UpdateStatus from "admin/dispatch/UpdateStatus";
import { useDispachCount } from "context/OrderCountProvider";
import imageCompression from "browser-image-compression";
import { getAuth } from "firebase/auth";
import { GenerateDriverRoutesPDF } from "admin/DriverBilling/GeneratePDF";
import { DriverRoutesRequest } from "models/Driver";



// Firebase configuration
const firebaseConfig = {
  apiKey: environment.apiKey,
  authDomain: environment.authDomain,
  databaseURL: environment.databaseURL,
  projectId: environment.projectId,
  storageBucket: environment.storageBucket,
  messagingSenderId: environment.messagingSenderId,
  appId: environment.appId,
};
const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);

const db = getDatabase(app);

interface DriverLocation {
  driverId: string;
  isLoggedIn: boolean;
  lastLogout?: number;
  location?: {
    accuracy: number;
    heading: number;
    latitude: number;
    longitude: number;
    speed: number;
    timestamp: number;
  };
  currentOrder?: {
    orderId: string;
    status: string;
    timestamp: number;
  };
  routes?: {
    [key: string]: any;
  };
  completedRoutes?: string[];
}

interface DriverDetails {
  _id: string;
  customerId: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  vehicleType: string;
  verifiedStatus: string;
  isActive?: boolean;
  availability?: boolean | string; // may come as string or boolean
}

interface OrderBuckets {
  live: Order[];
  upcoming: Order[];
  current: Order[];
  pickup: Order[];
}

interface PickupLocation {
  lat: number;
  lng: number;
  orderId: string;
  address: string;
  orderData: Order;
  addressType?: string;
}

interface Route {
  orderId: string;
  type: string;
  status: string;
  lat: number;
  lng: number;
  distance?: number;
  duration?: string;
  estimatedArrival?: string;
  sequence?: number;
}


interface UpdateOrderStatusForm {
  shipperName?: string;
  receiverName?: string;
  returnName?: string;
  redeliverName?: string;
  notes?: string;
  status?: string;
  date?: string;
  time?: string;
  imageFile?: File | null;
}


interface UpdateStatusProps {
  handleStatusAssign: (data: UpdateOrderStatusForm) => Promise<void>;
}


type DriverFilterType = "online" | "offline" | "all";
type OrderFilterType = "all" | "live" | "upcoming" | "current" | "pickup";

// Helper to extract drivers from AdminService variants
const extractDriversFromResponse = (response: any): DriverDetails[] => {
  if (!response || !response.data) return [];
  const { data } = response;

  if (Array.isArray(data)) return data;
  if (data.drivers && Array.isArray(data.drivers)) return data.drivers;
  if (data.driverList && Array.isArray(data.driverList)) return data.driverList;
  if (data.data && Array.isArray(data.data)) return data.data;

  return [];
};

// 🔧 Normalize Mongo availability into a strict boolean
const normalizeAvailability = (value: unknown): boolean | undefined => {
  if (value === undefined || value === null) return undefined;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    if (v === "true" || v === "available" || v === "yes" || v === "1") return true;
    if (v === "false" || v === "unavailable" || v === "no" || v === "0") return false;
  }
  return undefined;
};

const DriverTracking: React.FC = () => {
  const [driverLocations, setDriverLocations] = useState<DriverLocation[]>([]);
  const [driverDetails, setDriverDetails] = useState<{ [key: string]: DriverDetails & { _availabilityBool?: boolean } }>({});
  // const [validatedDrivers, setValidatedDrivers] = useState<DriverLocation[]>([]);
  const [allMongoDrivers, setAllMongoDrivers] = useState<DriverDetails[]>([]);
  const [showDriverList, setShowDriverList] = useState(false);
  const [showOrderList, setShowOrderList] = useState(false);
  const [showRoutes, setShowRoutes] = useState<{ [key: string]: boolean }>({});
  const [driverFilter, setDriverFilter] = useState<DriverFilterType>("online");
  const [orderFilter, setOrderFilter] = useState<OrderFilterType>("all");
  const [showDriverFilterDropdown, setShowDriverFilterDropdown] = useState(false);
  const [showOrderFilterDropdown, setShowOrderFilterDropdown] = useState(false);
  const [showDriversOnMap, setShowDriversOnMap] = useState(true);
  const [showOrdersOnMap, setShowOrdersOnMap] = useState(true);

  const [orders, setOrders] = useState<OrderBuckets>({
    live: [],
    upcoming: [],
    current: [],
    pickup: [],
  });
  const [selectedDriver, setSelectedDriver] = useState<DriverLocation | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<PickupLocation | null>(null);

  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedOrderForAssign, setSelectedOrderForAssign] = useState<Order | null>(null);
  const [selectedDriverId, setSelectedDriverId] = useState<string | undefined>(undefined);

  const [addresses, setAddresses] = useState<{ [key: string]: string }>({});
  const [zoomLevel, setZoomLevel] = useState(6);
  const mapRef = useRef<google.maps.Map | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [orderSearchTerm, setOrderSearchTerm] = useState("");
  const googleMapsApiKey = environment.google_maps_key;

  const [showStatusUpdateModal, setShowStatusUpdateModal] = useState(false);
  const [selectedOrderForStatus, setSelectedOrderForStatus] = useState<Order | null>(null);
  const [orderStatus, setOrderStatus] = useState<OrderStatus | DispatchType | undefined>(undefined);
  // Add these with your other useState declarations
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dispachCount, setDispachCount] = useDispachCount();
  const [statusModalKey, setStatusModalKey] = useState(0);


  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey,
    libraries: ["places", "geometry"],
  });

  const validatedDrivers = useMemo(() => {
  return driverLocations.filter((d) => {
    const mongo = driverDetails[d.driverId];


    if (!mongo) return true;

 
    return mongo.isActive !== false;
  });
}, [driverLocations, driverDetails]);


  // Counts now rely on normalized availability
  const onlineDriversCount = useMemo(() => {
    return validatedDrivers.filter((d) => {
      const det = driverDetails[d.driverId];
      const avail = normalizeAvailability(det?.availability ?? det?._availabilityBool);
      return d.isLoggedIn && avail === true;
    }).length;
  }, [validatedDrivers, driverDetails]);

  const offlineDriversCount = useMemo(() => {
    return validatedDrivers.filter((d) => {
      const det = driverDetails[d.driverId];
      const avail = normalizeAvailability(det?.availability ?? det?._availabilityBool);
      return d.isLoggedIn && avail === false;
    }).length;
  }, [validatedDrivers, driverDetails]);

  const allDriversCount = useMemo(() => validatedDrivers.length, [validatedDrivers]);

  // Order counts
  const orderCounts = useMemo(() => ({
    all: orders.live.length + orders.upcoming.length + orders.current.length + orders.pickup.length,
    live: orders.live.length,
    upcoming: orders.upcoming.length,
    current: orders.current.length,
    pickup: orders.pickup.length
  }), [orders]);

  // Final filtered list used by both list AND map/clusters
  const filteredDrivers = useMemo(() => {
    let base: DriverLocation[] = [];

    switch (driverFilter) {
      case "online":
        base = validatedDrivers.filter((d) => {
          const det = driverDetails[d.driverId];
          const avail = normalizeAvailability(det?.availability ?? det?._availabilityBool);
          return d.isLoggedIn && (avail === true || avail === undefined);
        });
        break;

      case "offline":
        base = validatedDrivers.filter((d) => {
          const det = driverDetails[d.driverId];
          const avail = normalizeAvailability(det?.availability ?? det?._availabilityBool);
          return d.isLoggedIn && avail === false;
        });
        break;

      case "all":
      default:
        base = validatedDrivers.filter((d) => d.isLoggedIn);
        break;
    }

    // Text search
    const q = searchTerm.trim().toLowerCase();
    if (!q) return base;

    return base.filter((d) => {
      const det = driverDetails[d.driverId];
      const name = `${det?.firstName ?? ""} ${det?.lastName ?? ""}`.trim().toLowerCase();
      const id = d.driverId.toLowerCase();
      const vehicle = det?.vehicleType?.toLowerCase() ?? "";
      return name.includes(q) || id.includes(q) || vehicle.includes(q);
    });
  }, [validatedDrivers, driverDetails, searchTerm, driverFilter]);

  // Filtered orders based on order filter and search
  const getFilteredOrders = useMemo(() => {
    let base: Order[] = [];

    switch (orderFilter) {
      case "live":
        base = orders.live;
        break;
      case "upcoming":
        base = orders.upcoming;
        break;
      case "current":
        base = orders.current;
        break;
      case "pickup":
        base = orders.pickup;
        break;
      case "all":
      default:
        base = [...orders.live, ...orders.upcoming, ...orders.current, ...orders.pickup];
        break;
    }

    // Add tags for display
    const ordersWithTags = base.map((o) => ({
      ...o,
      tag: orders.live.includes(o)
        ? "LIVE"
        : orders.upcoming.includes(o)
          ? "UPCOMING"
          : orders.current.includes(o)
            ? "CURRENT"
            : "PICK-UP",
    }));

    // Apply search filter
    if (!orderSearchTerm.trim()) return ordersWithTags;

    const s = orderSearchTerm.toLowerCase();
    return ordersWithTags.filter(
      (order) =>
        order.uniqueId?.toLowerCase().includes(s) ||
        order.pickUpAddress?.city?.toLowerCase().includes(s) ||
        order.deliveryAddress?.city?.toLowerCase().includes(s) ||
        order.driverUser?.name?.toLowerCase().includes(s)
    );
  }, [orders, orderFilter, orderSearchTerm]);

  // UI helpers
  const getDriverStatusBadge = (driver: DriverLocation) => {
    const details = driverDetails[driver.driverId];
    const avail = normalizeAvailability(details?.availability ?? details?._availabilityBool);
    if (driver.isLoggedIn && avail === true) return <span className="badge bg-success">Online</span>;
    if (driver.isLoggedIn && avail === false) return <span className="badge bg-secondary">Offline</span>;
    return <span className="badge bg-secondary">Offline</span>;
  };

  const formatTime = (ts: number) =>
    new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });

  const getDriverStatusText = (driver: DriverLocation) => {
    const details = driverDetails[driver.driverId];
    const avail = normalizeAvailability(details?.availability ?? details?._availabilityBool);

    if (driver.isLoggedIn && avail === true) {
      return driver.location ? `Last seen: ${formatTime(driver.location.timestamp)}` : "Available (No GPS)";
    } else if (driver.isLoggedIn && avail === false) {
      const lastLogout = driver.lastLogout;
      if (lastLogout) return `Last seen: ${formatTime(lastLogout)}`;
      return "Offline";
    } else {
      return "Not Logged In";
    }
  };

  const isDriverOnline = (driver: DriverLocation) => {
    const details = driverDetails[driver.driverId];
    const avail = normalizeAvailability(details?.availability ?? details?._availabilityBool);
    return driver.isLoggedIn && avail === true;
  };

  const getDriverRoutes = (driver: DriverLocation): Route[] => {
    if (!driver.routes) return [];
    return Object.values(driver.routes).sort((a: Route, b: Route) => (a.sequence || 0) - (b.sequence || 0));
  };

  const getAssignedOrdersCount = (driver: DriverLocation): number =>
    getDriverRoutes(driver).filter((r) => r.status === "ASSIGNED").length;

  const getPickedUpOrdersCount = (driver: DriverLocation): number =>
    getDriverRoutes(driver).filter((r) => r.status === "PICKED_UP" || r.type === "dropoff").length;

  const toggleRoutes = (driverId: string) => setShowRoutes((p) => ({ ...p, [driverId]: !p[driverId] }));

  // const getAddressFromCoords = async (lat: number, lng: number): Promise<string> => {
  //   if (!window.google) return "Loading...";
  //   const geocoder = new window.google.maps.Geocoder();
  //   return new Promise((resolve) => {
  //     geocoder.geocode({ location: { lat, lng } }, (results, status) => {
  //       if (status === "OK" && results && results[0]) resolve(results[0].formatted_address);
  //       else resolve(`Location (${lat.toFixed(4)}, ${lng.toFixed(4)})`);
  //     });
  //   });
  // };

  const orderDataMap = useMemo(() => {
    const allOrders = [...orders.live, ...orders.upcoming, ...orders.current, ...orders.pickup];
    const mapping: { [key: string]: Order } = {};
    allOrders.forEach((order) => {
      if (order._id) mapping[order._id] = order;
      if (order.uniqueId) mapping[order.uniqueId] = order;
    });
    return mapping;
  }, [orders]);

  const getDisplayOrderId = (firebaseOrderId: string): string => {
    const order = orderDataMap[firebaseOrderId];
    return order?.uniqueId || firebaseOrderId;
  };

  const pickupLocations: PickupLocation[] = useMemo(() => {
    let baseOrders: Order[] = [];

    // Filter orders based on orderFilter
    switch (orderFilter) {
      case "live":
        baseOrders = orders.live;
        break;
      case "upcoming":
        baseOrders = orders.upcoming;
        break;
      case "current":
        baseOrders = orders.current;
        break;
      case "pickup":
        baseOrders = orders.pickup;
        break;
      case "all":
      default:
        baseOrders = [...orders.live, ...orders.upcoming, ...orders.current, ...orders.pickup];
        break;
    }

    // Apply search filter BEFORE mapping to locations
    if (orderSearchTerm.trim()) {
      const s = orderSearchTerm.toLowerCase();
      baseOrders = baseOrders.filter(
        (order) =>
          order.uniqueId?.toLowerCase().includes(s) ||
          order.pickUpAddress?.city?.toLowerCase().includes(s) ||
          order.deliveryAddress?.city?.toLowerCase().includes(s) ||
          order.driverUser?.name?.toLowerCase().includes(s)
      );
    }


    const base = baseOrders
      .filter((order) => {
        if (
          order.orderStatus === "picked" &&
          order.deliveryAddress &&
          order.deliveryAddress.lat &&
          order.deliveryAddress.lng
        ) {
          return true;
        }
        return order.pickUpAddress && order.pickUpAddress.lat && order.pickUpAddress.lng;
      })
      .map((order) => {
        const isPickedUp = order.orderStatus === "picked";
        const addressData = isPickedUp ? order.deliveryAddress : order.pickUpAddress;
        const addressType = isPickedUp ? "Delivery" : "Pickup";
        return {
          lat: Number(addressData!.lat),
          lng: Number(addressData!.lng),
          orderId: order.uniqueId || "N/A",
          address: addressData?.city || "Unknown address",
          orderData: order,
          addressType,
        };
      });

    if (zoomLevel >= 10) {
      return base.map((loc) => ({
        ...loc,
        lat: loc.lat + (Math.random() - 0.5) * 0.0002,
        lng: loc.lng + (Math.random() - 0.5) * 0.0002,
      }));
    }
    return base;
  }, [orders, zoomLevel, orderFilter, orderSearchTerm]);

  // Load orders
  useEffect(() => {
    AdminService.getAllTrackingOrders()
      .then((res) => setOrders(res.data))
      .catch(() => toast.error("Could not load orders"));
  }, []);

  // Load ALL Mongo drivers (optional list)
  // useEffect(() => {
  //   const loadAllDrivers = async () => {
  //     try {
  //       const response = await AdminService.getDrivers();
  //       const drivers = extractDriversFromResponse(response);
  //       setAllMongoDrivers(drivers);
  //     } catch (error) {
  //       console.error("Failed to load all drivers:", error);
  //       toast.error("Failed to load drivers list");
  //     }
  //   };
  //   loadAllDrivers();
  // }, []);

  // Validate Firebase online drivers against Mongo; build details map with normalized availability
const onlineIdsKey = useMemo(() => {
  return driverLocations.map(d => d.driverId).sort().join(",");
}, [driverLocations]);

useEffect(() => {
  const validateWithMongo = async () => {
    if (!onlineIdsKey) {
      setDriverDetails({});
      return;
    }

    try {
      const ids = onlineIdsKey.split(",").filter(Boolean);
      const response = await AdminService.getDriversByCustomerIds(ids);

      const mongoDriversData = extractDriversFromResponse(response) || response.data;

      const map: Record<string, any> = {};
      if (Array.isArray(mongoDriversData)) {
        mongoDriversData.forEach((driver: DriverDetails) => {
          if (driver.customerId) map[driver.customerId] = driver;
        });
      }

      setDriverDetails(map);
    } catch (e) {
      console.error(e);
    }
  };

  validateWithMongo();
}, [onlineIdsKey]);



  // Firebase realtime locations (only logged in + has location)
  useEffect(() => {
    const driversRef = ref(db, "drivers");
    const unsubscribe = onValue(driversRef, (snapshot) => {
      const driversData = snapshot.val();
      const drivers: DriverLocation[] = [];

      if (driversData) {
        Object.keys(driversData).forEach((driverId) => {
          const driver = driversData[driverId];
          if (driver?.authState?.isLoggedIn && driver.location) {
            drivers.push({
              driverId,
              isLoggedIn: driver.authState.isLoggedIn,
              lastLogout: driver.authState.lastLogout,
              location: driver.location,
              currentOrder: driver.currentOrder,
              routes: driver.routes,
              completedRoutes: driver.completedRoutes || [],
            });
          }
        });
      }
      setDriverLocations(drivers);
    });
    return () => {
      off(driversRef);
      unsubscribe();
    };
  }, []);

  const statuscount = async () => {
    await AdminService.getalldispachcount().then(res => {
      if (res.success) {
        setDispachCount(res.data);
      } else {
      }
    });
  };

  const downloadDriverRoutes = async (
    driverId: string,
    routes: Route[]
  ) => {
    const routeData: DriverRoutesRequest = {
      driverId: driverId,
      routes: routes,
    };
    GenerateDriverRoutesPDF(routeData);
  };

  // Reverse geocode visible driver addresses (filtered list)
  // useEffect(() => {
  //   if (!isLoaded || filteredDrivers.length === 0) return;

  //   const fetchAddresses = async () => {
  //     const newAddresses: { [key: string]: string } = {};
  //     for (const driver of filteredDrivers) {
  //       if (driver.location) {
  //         const address = await getAddressFromCoords(driver.location.latitude, driver.location.longitude);
  //         newAddresses[driver.driverId] = address;
  //       }
  //     }
  //     setAddresses((prev) => ({ ...prev, ...newAddresses }));
  //   };

  //   fetchAddresses();
  // }, [isLoaded, filteredDrivers]);

  // Google Maps configuration
  const mapContainerStyle = { width: "100%", height: "600px" };
  const mapCenter = useMemo(() => ({ lat: 43.6532, lng: -79.3832 }), []);

  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumb title="Driver Tracking" breadcrumbItem="Driver Tracking" />
          <div className="text-center mt-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading map...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Driver Tracking" breadcrumbItem="Driver Tracking" />

        {/* Google Map Section */}
        <Col lg={12} className="mb-4">
          <Card>
            <CardBody>
              {/* Controls */}
              <div
                style={{
                  position: "absolute",
                  top: "20px",
                  left: "20px",
                  zIndex: 10,
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  background: "rgba(255, 255, 255, 0.95)",
                  padding: "10px 15px",
                  borderRadius: "12px",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                }}
              >
                {/* Date selector (placeholder) */}
                <div
                  style={{
                    background: "#fff",
                    padding: "6px 12px",
                    borderRadius: "8px",
                    border: "1px solid #dee2e6",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Input type="date" value={new Date().toISOString().split("T")[0]} style={{ border: "none", outline: "none" }} readOnly />
                </div>

                {/* Show/Hide Drivers on Map Toggle */}
                <div
                  style={{
                    background: "#fff",
                    padding: "6px 12px",
                    borderRadius: "8px",
                    border: "1px solid #dee2e6",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <span className="small">Show Drivers</span>
                  <div className="form-check form-switch mb-0">
                    <Input
                      type="checkbox"
                      role="switch"
                      id="toggleDriversOnMap"
                      checked={showDriversOnMap}
                      onChange={() => setShowDriversOnMap(!showDriversOnMap)}
                      style={{ cursor: "pointer" }}
                    />
                  </div>
                </div>

                {/* Driver Filter Dropdown */}
                <div style={{ position: "relative" }}>
                  <Button
                    size="sm"
                    color="light"
                    className="d-flex align-items-center gap-1"
                    onClick={() => setShowDriverFilterDropdown(!showDriverFilterDropdown)}
                  >
                    <FaFilter />
                    {showDriverFilterDropdown ? <FiChevronUp /> : <FiChevronDown />}
                  </Button>

                  {showDriverFilterDropdown && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: "0",
                        marginTop: "8px",
                        background: "white",
                        borderRadius: "8px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        padding: "12px",
                        minWidth: "200px",
                        zIndex: 1000,
                      }}
                    >
                      <div className="d-flex flex-column gap-2">
                        <Button
                          size="sm"
                          color={driverFilter === "online" ? "primary" : "light"}
                          onClick={() => {
                            setDriverFilter("online");
                            setShowDriverFilterDropdown(false);
                          }}
                          className="d-flex align-items-center justify-content-between"
                        >
                          <div className="d-flex align-items-center gap-2">
                            <FaUserTie className="text-success" />
                            <span>Online</span>
                          </div>
                          <span className="badge bg-success">{onlineDriversCount}</span>
                        </Button>

                        <Button
                          size="sm"
                          color={driverFilter === "offline" ? "secondary" : "light"}
                          onClick={() => {
                            setDriverFilter("offline");
                            setShowDriverFilterDropdown(false);
                          }}
                          className="d-flex align-items-center justify-content-between"
                        >
                          <div className="d-flex align-items-center gap-2">
                            <FaUserSlash className="text-secondary" />
                            <span>Offline</span>
                          </div>
                          <span className="badge bg-secondary">{offlineDriversCount}</span>
                        </Button>

                        <Button
                          size="sm"
                          color={driverFilter === "all" ? "info" : "light"}
                          onClick={() => {
                            setDriverFilter("all");
                            setShowDriverFilterDropdown(false);
                          }}
                          className="d-flex align-items-center justify-content-between"
                        >
                          <div className="d-flex align-items-center gap-2">
                            <FaUsers className="text-info" />
                            <span>All Drivers</span>
                          </div>
                          <span className="badge bg-info">{allDriversCount}</span>
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Show/Hide Orders on Map Toggle */}
                <div
                  style={{
                    background: "#fff",
                    padding: "6px 12px",
                    borderRadius: "8px",
                    border: "1px solid #dee2e6",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <span className="small">Show Orders</span>
                  <div className="form-check form-switch mb-0">
                    <Input
                      type="checkbox"
                      role="switch"
                      id="toggleOrdersOnMap"
                      checked={showOrdersOnMap}
                      onChange={() => setShowOrdersOnMap(!showOrdersOnMap)}
                      style={{ cursor: "pointer" }}
                    />
                  </div>
                </div>



                {/* Order Filter Dropdown */}
                <div style={{ position: "relative" }}>
                  <Button
                    size="sm"
                    color="light"
                    className="d-flex align-items-center gap-1"
                    onClick={() => setShowOrderFilterDropdown(!showOrderFilterDropdown)}
                  >
                    <FaFilter />
                    {showOrderFilterDropdown ? <FiChevronUp /> : <FiChevronDown />}
                  </Button>

                  {showOrderFilterDropdown && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: "0",
                        marginTop: "8px",
                        background: "white",
                        borderRadius: "8px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        padding: "12px",
                        minWidth: "200px",
                        zIndex: 1000,
                      }}
                    >
                      <div className="d-flex flex-column gap-2">
                        <Button
                          size="sm"
                          color={orderFilter === "all" ? "primary" : "light"}
                          onClick={() => {
                            setOrderFilter("all");
                            setShowOrderFilterDropdown(false);
                          }}
                          className="d-flex align-items-center justify-content-between"
                        >
                          <div className="d-flex align-items-center gap-2">
                            <FaBox className="text-primary" />
                            <span>All Orders</span>
                          </div>
                          <span className="badge bg-primary">{orderCounts.all}</span>
                        </Button>

                        <Button
                          size="sm"
                          color={orderFilter === "live" ? "danger" : "light"}
                          onClick={() => {
                            setOrderFilter("live");
                            setShowOrderFilterDropdown(false);
                          }}
                          className="d-flex align-items-center justify-content-between"
                        >
                          <div className="d-flex align-items-center gap-2">
                            <FaBox className="text-danger" />
                            <span>Live</span>
                          </div>
                          <span className="badge bg-danger">{orderCounts.live}</span>
                        </Button>

                        <Button
                          size="sm"
                          color={orderFilter === "upcoming" ? "warning" : "light"}
                          onClick={() => {
                            setOrderFilter("upcoming");
                            setShowOrderFilterDropdown(false);
                          }}
                          className="d-flex align-items-center justify-content-between"
                        >
                          <div className="d-flex align-items-center gap-2">
                            <FaBox className="text-warning" />
                            <span>Upcoming</span>
                          </div>
                          <span className="badge bg-warning">{orderCounts.upcoming}</span>
                        </Button>

                        <Button
                          size="sm"
                          color={orderFilter === "current" ? "info" : "light"}
                          onClick={() => {
                            setOrderFilter("current");
                            setShowOrderFilterDropdown(false);
                          }}
                          className="d-flex align-items-center justify-content-between"
                        >
                          <div className="d-flex align-items-center gap-2">
                            <FaBox className="text-info" />
                            <span>Current</span>
                          </div>
                          <span className="badge bg-info">{orderCounts.current}</span>
                        </Button>

                        <Button
                          size="sm"
                          color={orderFilter === "pickup" ? "success" : "light"}
                          onClick={() => {
                            setOrderFilter("pickup");
                            setShowOrderFilterDropdown(false);
                          }}
                          className="d-flex align-items-center justify-content-between"
                        >
                          <div className="d-flex align-items-center gap-2">
                            <FaBox className="text-success" />
                            <span>Pickup</span>
                          </div>
                          <span className="badge bg-success">{orderCounts.pickup}</span>
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Panel (Drivers & Orders) */}
              <div
                style={{
                  position: "absolute",
                  top: "0px",
                  right: "20px",
                  zIndex: 1000,
                  width: "450px",
                  maxHeight: "70vh",
                  overflowY: "auto",
                }}
              >
                <Card className="shadow-lg">
                  <CardBody className="p-3">
                    {/* Drivers Section */}
                    <div className="mb-4">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0 d-flex align-items-center text-dark">
                          <span
                            className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-2"
                            style={{ width: "32px", height: "32px" }}
                          >
                            <FaUserTie />
                          </span>
                          {driverFilter === "online" && `Online Drivers (${filteredDrivers.length})`}
                          {driverFilter === "offline" && `Offline Drivers (${filteredDrivers.length})`}
                          {driverFilter === "all" && `All Drivers (${filteredDrivers.length})`}
                        </h6>
                        <Button size="sm" color="light" onClick={() => setShowDriverList(!showDriverList)}>
                          {showDriverList ? <FiChevronUp /> : <FiChevronDown />}
                        </Button>
                      </div>

                      {showDriverList && (
                        <div>
                          <div className="mb-3 position-relative">
                            <FaSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                            <Input
                              type="text"
                              placeholder="Search drivers..."
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              className="ps-5"
                            />
                          </div>

                          <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                            {filteredDrivers.length === 0 ? (
                              <p className="text-muted text-center py-3">
                                {driverFilter === "online" ? "No online drivers" : driverFilter === "offline" ? "No offline drivers" : "No drivers found"}
                              </p>
                            ) : (
                              filteredDrivers.map((driver) => {
                                const details = driverDetails[driver.driverId];
                                const routes = getDriverRoutes(driver);
                                const assignedOrders = getAssignedOrdersCount(driver);
                                const pickedUpOrders = getPickedUpOrdersCount(driver);
                                const online = isDriverOnline(driver);

                                return (
                                  <div key={driver.driverId} className="card mb-3 border">
                                    <div className="card-body">
                                      <div className="d-flex justify-content-between align-items-start mb-2">
                                        <div className="d-flex align-items-center">
                                          <div
                                            className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-2"
                                            style={{ width: "40px", height: "40px", fontWeight: "bold" }}
                                          >
                                            {details ? (details.firstName || driver.driverId).charAt(0).toUpperCase() : driver.driverId.charAt(0).toUpperCase()}
                                          </div>
                                          <div>
                                            <strong className="d-block">{driver.driverId}</strong>
                                            {details && <small className="text-muted">{details.firstName} {details.lastName}</small>}
                                            <small className="d-block text-muted">{details?.vehicleType || "Unknown vehicle"}</small>
                                            <div className="mt-1">{getDriverStatusBadge(driver)}</div>
                                          </div>
                                        </div>
                                        <div className="text-end">
                                          <small className="text-muted d-block">{getDriverStatusText(driver)}</small>
                                          {online && (
                                            <Button
                                              size="sm"
                                              color={showRoutes[driver.driverId] ? "warning" : "outline-primary"}
                                              onClick={() => {
                                                toggleRoutes(driver.driverId);
                                                setSelectedDriver(driver);
                                              }}
                                            >
                                              <FaRoute className="me-1" />
                                              {showRoutes[driver.driverId] ? "Hide Routes" : "Show Routes"}
                                            </Button>
                                          )}
                                        </div>
                                      </div>
                                      {/* 
                                      {driver.location && (
                                        <div className="mb-2">
                                          <small className="text-muted">
                                            <FaMapMarkerAlt className="me-1" />
                                            {addresses[driver.driverId] || "Loading location..."}
                                          </small>
                                        </div>
                                      )} */}

                                      {online && (
                                        <div className="row text-center g-2 mb-2">
                                          <div className="col-4">
                                            <div className="border rounded py-1 bg-light">
                                              <div className="small text-muted">Routes</div>
                                              <div className="fw-bold text-primary">{assignedOrders}</div>
                                            </div>
                                          </div>
                                          <div className="col-4">
                                            <div className="border rounded py-1 bg-light">
                                              <div className="small text-muted">To Pick</div>
                                              <div className="fw-bold text-success">{pickedUpOrders}</div>
                                            </div>
                                          </div>
                                          <div className="col-4">
                                            <div className="border rounded py-1 bg-light">
                                              <div className="small text-muted">Completed</div>
                                              <div className="fw-bold text-info">{driver.completedRoutes?.length || 0}</div>
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Orders Section */}
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0 d-flex align-items-center text-dark">
                          <span
                            className="bg-warning text-white rounded-circle d-flex align-items-center justify-content-center me-2"
                            style={{ width: "32px", height: "32px" }}
                          >
                            <FaBox />
                          </span>
                          {orderFilter === "all" && `All Orders (${getFilteredOrders.length})`}
                          {orderFilter === "live" && `Live Orders (${getFilteredOrders.length})`}
                          {orderFilter === "upcoming" && `Upcoming Orders (${getFilteredOrders.length})`}
                          {orderFilter === "current" && `Current Orders (${getFilteredOrders.length})`}
                          {orderFilter === "pickup" && `Pickup Orders (${getFilteredOrders.length})`}
                        </h6>
                        <Button size="sm" color="light" onClick={() => setShowOrderList(!showOrderList)}>
                          {showOrderList ? <FiChevronUp /> : <FiChevronDown />}
                        </Button>
                      </div>

                      {showOrderList && (
                        <div>
                          <div className="mb-3 position-relative">
                            <FaSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                            <Input
                              type="text"
                              placeholder="Search orders by ID, city, or driver..."
                              value={orderSearchTerm}
                              onChange={(e) => setOrderSearchTerm(e.target.value)}
                              className="ps-5"
                            />
                          </div>

                          <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                            {getFilteredOrders.length === 0 ? (
                              <p className="text-muted text-center py-3">No orders found</p>
                            ) : (
                              getFilteredOrders.map((order) => (
                                <div key={order._id} className="card mb-2 border">
                                  <div className="card-body p-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                      <div className="d-flex align-items-center">
                                        <span
                                          className={`rounded-circle me-2 ${order.tag === "LIVE" ? "bg-danger" : order.tag === "CURRENT" ? "bg-primary" : "bg-success"
                                            }`}
                                          style={{ width: "12px", height: "12px" }}
                                        />
                                        <strong>{order.uniqueId}</strong>
                                      </div>
                                    </div>

                                    <div className="row g-2 mb-2">
                                      <div className="col-6">
                                        <small className="text-muted">Pickup</small>
                                        <div className="fw-light small">{order.pickUpAddress?.city}</div>
                                      </div>
                                      <div className="col-6 text-end">
                                        <small className="text-muted">Delivery</small>
                                        <div className="fw-light small">{order.deliveryAddress?.city}</div>
                                      </div>
                                    </div>

                                    {order.driverUser?.name && (
                                      <small className="text-muted d-block mb-2">👤 {order.driverUser.name}</small>
                                    )}

                                    <div className="d-flex justify-content-between align-items-center">
                                      <small className="text-muted">
                                        {order.packageReadyTime
                                          ? new Date(order.packageReadyTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                                          : "—"}
                                      </small>
                                      <Button
                                        size="sm"
                                        color={order.tag === "LIVE" || order.tag === "UPCOMING" ? "primary" : "warning"}
                                        onClick={() => {
                                          setSelectedOrderForAssign(order);
                                          setAssignModalOpen(true);
                                        }}
                                      >
                                        {order.tag === "LIVE" || order.tag === "UPCOMING" ? "Assign" : "Reassign"}
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardBody>
                </Card>
              </div>

              {/* Google Map */}
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                zoom={zoomLevel}
                center={mapCenter}
                onLoad={(map) => void (mapRef.current = map)}
                onZoomChanged={() => {
                  const newZoom = mapRef.current?.getZoom();
                  if (newZoom) setZoomLevel(newZoom);
                }}
                options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: true }}
              >
                {/* Driver markers - Independent filtering */}
                {/* Driver markers - Independent filtering with HIGHLIGHTED SELECTED DRIVER */}
                {showDriversOnMap &&
                  filteredDrivers
                    .filter((driver) => driver.location) // Only show drivers with location data
                    .map((driver) => {
                      const det = driverDetails[driver.driverId];
                      const avail = normalizeAvailability(det?.availability ?? det?._availabilityBool);
                      const isSelected = selectedDriver?.driverId === driver.driverId;

                      const iconUrl = isSelected
                        ? "https://maps.google.com/mapfiles/ms/icons/green-dot.png" // Green for selected driver
                        : avail === true
                          ? "https://maps.google.com/mapfiles/ms/icons/blue-dot.png" // Blue for online
                          : "https://maps.google.com/mapfiles/ms/icons/ltblue-dot.png"; // Light blue for offline

                      const markerSize = isSelected ? 50 : 40;

                      return (
                        <MarkerF
                          key={`driver-${driver.driverId}`}
                          position={{
                            lat: driver.location!.latitude,
                            lng: driver.location!.longitude,
                          }}
                          title={`Driver: ${driver.driverId} (${avail ? 'Online' : 'Offline'}${isSelected ? ' - SELECTED' : ''})`}
                          icon={{
                            url: iconUrl,
                            scaledSize: new window.google.maps.Size(markerSize, markerSize),
                          }}
                          onClick={() => setSelectedDriver(driver)}
                          zIndex={isSelected ? 1000 : 1}
                        />
                      );
                    })}

                {/* Numbered route markers for selected driver */}
                {selectedDriver &&
                  showRoutes[selectedDriver.driverId] &&
                  getDriverRoutes(selectedDriver).map((route, idx) => {
                    const sequenceNumber = route.sequence ?? idx + 1;
                    const icon = {
                      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                          <svg width="40" height="40" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="20" cy="20" r="18" fill="#007bff" stroke="white" stroke-width="3"/>
                            <text x="20" y="26" text-anchor="middle" fill="white" font-size="16" font-weight="bold" font-family="Arial, sans-serif">${sequenceNumber}</text>
                          </svg>
                        `)}`,
                      scaledSize: new window.google.maps.Size(40, 40),
                      anchor: new window.google.maps.Point(20, 20),
                    };
                    return (
                      <MarkerF
                        key={`route-${route.orderId}-${idx}`}
                        position={{ lat: route.lat, lng: route.lng }}
                        title={`Stop ${sequenceNumber}: ${route.type.toUpperCase()} - ${route.orderId}`}
                        icon={icon}
                      />
                    );
                  })}

                {/* Pickup markers - Independent filtering */}
                {showOrdersOnMap &&
                  pickupLocations.map((location, index) => (
                    <MarkerF
                      key={`pickup-${index}`}
                      position={{ lat: location.lat, lng: location.lng }}
                      title={`Order: ${location.orderId} - ${location.address}`}
                      icon={{ url: "https://maps.google.com/mapfiles/ms/icons/orange-dot.png", scaledSize: new window.google.maps.Size(40, 40) }}
                      onClick={() => setSelectedOrder(location)}
                    />
                  ))}
              </GoogleMap>
            </CardBody>
          </Card>
        </Col>

        {/* BELOW MAP SECTIONS */}
        <Row className="mt-4">
          {/* Driver Information Panel (Below Map) */}
          {selectedDriver && (
            <Col lg={6} className="mb-4">
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">
                      <FaUserTie className="me-2 text-primary" />
                      Driver Information
                    </h5>
                    <Button size="sm" color="light" onClick={() => setSelectedDriver(null)}>
                      Close
                    </Button>
                  </div>

                  <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                    <div className="mb-3">
                      <p className="mb-2">
                        <strong>Driver ID:</strong> {selectedDriver.driverId}
                      </p>
                      {driverDetails[selectedDriver.driverId] && (
                        <>
                          <p className="mb-2">
                            <strong>Name:</strong> {driverDetails[selectedDriver.driverId].firstName} {driverDetails[selectedDriver.driverId].lastName}
                          </p>
                          <p className="mb-2">
                            <strong>Vehicle:</strong> {driverDetails[selectedDriver.driverId].vehicleType}
                          </p>
                          <p className="mb-2">
                            <strong>Availability:</strong>{" "}
                            {normalizeAvailability(
                              driverDetails[selectedDriver.driverId].availability ??
                              driverDetails[selectedDriver.driverId]?._availabilityBool
                            )
                              ? "Online"
                              : "Offline"}
                          </p>
                        </>
                      )}

                      {selectedDriver.currentOrder && (
                        <>
                          <p className="mb-2">
                            <strong>Current Order:</strong> {getDisplayOrderId(selectedDriver.currentOrder.orderId)}
                          </p>
                          <p className="mb-2">
                            <strong>Status:</strong> {selectedDriver.currentOrder.status.replace(/_/g, " ").toUpperCase()}
                          </p>
                        </>
                      )}

                      <p className="mb-2">
                        <strong>Routes:</strong> {getAssignedOrdersCount(selectedDriver)}
                      </p>
                      <p className="mb-2">
                        <strong>To Pick:</strong> {getPickedUpOrdersCount(selectedDriver)}
                      </p>
                      <Button
                        size="sm"
                        color="primary"
                        onClick={() => toggleRoutes(selectedDriver.driverId)}
                        className="mt-2"
                      >
                        {showRoutes[selectedDriver.driverId] ? "Hide Numbered Routes" : "Show Numbered Routes"}
                      </Button>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          )}

          {/* Order Information Panel (Below Map) */}
          {selectedOrder && (
            <Col lg={6} className="mb-4">
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">
                      <FaBox className="me-2 text-warning" />
                      Order Information
                    </h5>
                    <Button size="sm" color="light" onClick={() => setSelectedOrder(null)}>
                      Close
                    </Button>
                  </div>

                  <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                    <div className="mb-3">
                      <p className="mb-2">
                        <strong>Order ID:</strong> {selectedOrder.orderId}
                      </p>

                      {selectedOrder.orderData.orderStatus === "picked" ? (
                        <>
                          <p className="mb-2">
                            <strong>Delivery Address:</strong> {selectedOrder.orderData.deliveryAddress?.city || "Unknown"}
                          </p>
                          <p className="mb-2">
                            <strong>Status:</strong> IN TRANSIT (Picked Up)
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="mb-2">
                            <strong>Pickup Address:</strong> {selectedOrder.orderData.pickUpAddress?.city || "Unknown"}
                          </p>
                          <p className="mb-2">
                            <strong>Delivery Address:</strong> {selectedOrder.orderData.deliveryAddress?.city || "Unknown"}
                          </p>
                          <p className="mb-2">
                            <strong>Status:</strong>{" "}
                            {orders.live.some((o) => o._id === selectedOrder.orderData._id)
                              ? "LIVE"
                              : orders.upcoming.some((o) => o._id === selectedOrder.orderData._id)
                                ? "UPCOMING"
                                : orders.current.some((o) => o._id === selectedOrder.orderData._id)
                                  ? "CURRENT"
                                  : "PICK-UP"}
                          </p>
                        </>
                      )}

                      <div className="mt-3">
                        <Button
                          size="sm"
                          color={
                            orders.live.some((o) => o._id === selectedOrder.orderData._id) ||
                              orders.upcoming.some((o) => o._id === selectedOrder.orderData._id)
                              ? "primary"
                              : "warning"
                          }
                          onClick={() => {
                            setSelectedOrderForAssign(selectedOrder.orderData);
                            setAssignModalOpen(true);
                          }}
                          className="w-100 mb-2"
                        >
                          {orders.live.some((o) => o._id === selectedOrder.orderData._id) ||
                            orders.upcoming.some((o) => o._id === selectedOrder.orderData._id)
                            ? "Assign Driver"
                            : "Reassign Driver"}
                        </Button>

                        <Button
                          size="sm"
                          color="danger"
                          className="w-100"
                          onClick={() => {
                            setSelectedOrderForStatus(selectedOrder.orderData);
                            setOrderStatus(selectedOrder.orderData.orderStatus as OrderStatus);
                            setShowStatusUpdateModal(true);
                          }}
                        >
                          Update Order Status
                        </Button>
                      </div>

                      {selectedOrder.orderData.driverUser?.name && (
                        <div className="mt-3">
                          <small className="text-muted">
                            <strong>Current Driver:</strong> {selectedOrder.orderData.driverUser.name}
                          </small>
                        </div>
                      )}

                      {selectedOrder.orderData.packageReadyTime && (
                        <div className="mt-2">
                          <small className="text-muted">
                            <strong>Ready Time:</strong>{" "}
                            {new Date(selectedOrder.orderData.packageReadyTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </small>
                        </div>
                      )}
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          )}

          {/* Routes Panel (Below Map) */}
          {Object.keys(showRoutes).some((driverId) => showRoutes[driverId]) && (
            <Col lg={12} className="mb-4">
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">
                      <FaRoute className="me-2 text-primary" />
                      Driver Routes
                    </h5>
                    <Button size="sm" color="light" onClick={() => setShowRoutes({})}>
                      Close All
                    </Button>
                  </div>

                  <div style={{ maxHeight: "500px", overflowY: "auto" }}>
                    <Row>
                      {validatedDrivers
                        .filter((d) => showRoutes[d.driverId] && isDriverOnline(d))
                        .map((driver) => {
                          const details = driverDetails[driver.driverId];
                          const routes = getDriverRoutes(driver);
                          return (
                            <Col lg={6} key={driver.driverId} className="mb-3">
                              <Card className="border">
                                <CardBody>
                                  <div className="d-flex align-items-center mb-3 p-2 bg-light rounded">
                                    <div
                                      className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-2"
                                      style={{ width: "32px", height: "32px", fontWeight: "bold" }}
                                    >
                                      {details ? (details.firstName || driver.driverId).charAt(0).toUpperCase() : driver.driverId.charAt(0).toUpperCase()}
                                    </div>
                                    <div className="flex-grow-1">
                                      <strong className="d-block">{driver.driverId}</strong>
                                      {details && <small className="text-muted">{details.firstName} {details.lastName}</small>}
                                    </div>
                                    <Button size="sm" color="outline-danger" className="me-2" onClick={() => downloadDriverRoutes(details._id, routes)}>
                                      <FiDownload />
                                    </Button>
                                    <Button size="sm" color="outline-danger" onClick={() => toggleRoutes(driver.driverId)}>
                                      <FiX />
                                    </Button>
                                  </div>

                                  <div className="routes-list">
                                    {routes.map((route, index) => {
                                      const orderData = orderDataMap[route.orderId];
                                      const bgClass =
                                        route.type === "pickup"
                                          ? "bg-info bg-opacity-50"
                                          : route.type === "dropoff"
                                            ? "bg-success bg-opacity-50"
                                            : "bg-white";
                                      return (
                                        <div key={index} className={`card mb-2 border ${bgClass}`}>
                                          <div className="card-body p-2">
                                            <div className="d-flex justify-content-between align-items-start">
                                              <div className="flex-grow-1">
                                                <div className="d-flex align-items-center gap-2 mb-1">
                                                  {route.sequence && <span className="badge bg-primary">Stop {route.sequence}</span>}
                                                  <small className="fw-bold">{orderData?.uniqueId || route.orderId}</small>
                                                </div>
                                                <small className="text-muted d-block text-uppercase">{route.type}</small>
                                                {orderData && (
                                                  <small className="text-muted d-block">
                                                    📍 {route.type == 'pickup' ? orderData.pickUpAddress?.city : route.type == 'dropoff' ? orderData.deliveryAddress?.city : ''}
                                                  </small>
                                                )}
                                              </div>
                                              <span className={`badge bg-${route.status === "ASSIGNED" ? "warning" : "success"}`}>{route.status}</span>
                                            </div>
                                            <div className="d-flex justify-content-between mt-1">
                                              {route.estimatedArrival && (
                                                <small className="text-muted">
                                                  🕒 {new Date(route.estimatedArrival).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                                </small>
                                              )}
                                              {route.distance && <small className="text-muted">📏 {(route.distance / 1000).toFixed(1)} km</small>}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </CardBody>
                              </Card>
                            </Col>
                          );
                        })}
                    </Row>
                  </div>
                </CardBody>
              </Card>
            </Col>
          )}
        </Row>

        {/* Assign Driver Modal */}
        <div style={{ zIndex: 99999, position: "fixed" }}>
          <AssignDriver
            isOpen={assignModalOpen}
            toggle={() => setAssignModalOpen(!assignModalOpen)}
            onSelectedDriverChange={(driverId) => setSelectedDriverId(driverId)}
            handleDriverAssign={async () => {
              if (!selectedDriverId || !selectedOrderForAssign?._id) {
                toast.warn("Please select a driver");
                return;
              }
              try {
                const payload = { orderIds: [selectedOrderForAssign._id], driverId: selectedDriverId };
                const res = await AdminService.postDriverByOrder(payload);

                if (res.success) {
                  toast.success("Driver reassigned successfully!");
                  const updatedOrdersRes = await AdminService.getAllTrackingOrders();
                  setOrders(updatedOrdersRes.data);

                  // keep info windows in sync
                  if (selectedOrder && selectedOrder.orderData._id === selectedOrderForAssign._id) {
                    const allUpdated = [
                      ...updatedOrdersRes.data.live,
                      ...updatedOrdersRes.data.upcoming,
                      ...updatedOrdersRes.data.current,
                      ...updatedOrdersRes.data.pickup,
                    ];
                    const updatedOrder = allUpdated.find((o) => o._id === selectedOrderForAssign._id);
                    if (updatedOrder) setSelectedOrder({ ...selectedOrder, orderData: updatedOrder });
                  }
                  const allUpdated2 = [
                    ...updatedOrdersRes.data.live,
                    ...updatedOrdersRes.data.upcoming,
                    ...updatedOrdersRes.data.current,
                    ...updatedOrdersRes.data.pickup,
                  ];
                  const updatedForAssign = allUpdated2.find((o) => o._id === selectedOrderForAssign._id);
                  if (updatedForAssign) setSelectedOrderForAssign(updatedForAssign);
                } else {
                  toast.error(res.error || "Failed to reassign driver");
                }

                setAssignModalOpen(false);
                setSelectedDriverId(undefined);
                setSelectedOrderForAssign(null);
              } catch (error) {
                console.error(error);
                toast.error("Failed to reassign driver");
              }
            }}
            handleDriverPush={() => console.log("Push action (optional)")}
            isLiveOrdersPage={false}
          />
        </div>



        <UpdateStatus
          key={statusModalKey}
          isOpen={showStatusUpdateModal}
          toggle={() => {
            setShowStatusUpdateModal(false);
            setIsUpdatingStatus(false);
          }}
          currentStatus={orderStatus!}
          onSelectedStatusChange={(status) => {
            if (!status) return;


            if (status === OrderStatus.RETURN) {
              setOrderStatus(OrderStatus.RETURNED);
            } else if (status === OrderStatus.REATTEMPT) {
              setOrderStatus(OrderStatus.Reattempted);
            } else {
              setOrderStatus(status as OrderStatus);
            }
          }}

          handleStatusAssign={async (data: UpdateOrderStatusForm) => {
            if (!selectedOrderForStatus?._id) return;

            try {
              setIsUpdatingStatus(true);
              setLoading(true);


              const formData = new FormData();

              formData.append("orderId", selectedOrderForStatus._id);
              formData.append("status", data.status || "");
              formData.append("notes", data.notes || "");
              formData.append("receiverName", data.receiverName || "");
              formData.append("shipperName", data.shipperName || "");
              formData.append("returnName", data.returnName || "");
              formData.append("redeliverName", data.redeliverName || "");
              formData.append("date", data.date || "");
              formData.append("time", data.time || "");


              if (data.imageFile) {
                if (data.imageFile.type === "image/webp") {
                  try {
                    const options = {
                      maxSizeMB: 1,
                      maxWidthOrHeight: 1920,
                      useWebWorker: true,
                      fileType: 'image/jpeg',
                    };

                    const convertedImage = await imageCompression(data.imageFile, options);
                    formData.append("image", convertedImage);
                  } catch (error) {
                    console.error("Error converting WebP to JPEG/PNG:", error);
                    toast.error("Error processing image.");
                    setIsUpdatingStatus(false);
                    setLoading(false);
                    return;
                  }
                } else {
                  formData.append("image", data.imageFile);
                }
              }


              const res = await AdminService.updateOrderStatus(formData as any);

              if (res.success) {
                toast.success("Order status updated successfully!");

                const updatedOrdersRes = await AdminService.getAllTrackingOrders();
                setOrders(updatedOrdersRes.data);


                setSelectedOrder(null);
                setSelectedDriver(null);
                setStatusModalKey((k) => k + 1);
                setShowStatusUpdateModal(false);
                setSelectedOrderForStatus(null);
                setOrderStatus(undefined);


                await statuscount();


                setSelectedOrderForStatus(null);
                setOrderStatus(undefined);
              } else {
                toast.error(res.error || "Failed to update order status");
              }
            } catch (error: any) {
              console.error("Error updating order status:", error);
              toast.error(error.message || "An error occurred while updating order status");
            } finally {

              setIsUpdatingStatus(false);
              setLoading(false);
            }
          }}

        />

      </div>



      {isUpdatingStatus && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999999,
          }}
        >
          <div
            style={{
              background: 'white',
              padding: '30px',
              borderRadius: '10px',
              textAlign: 'center',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            }}
          >
            <div
              className="spinner-border text-primary"
              style={{ width: '3rem', height: '3rem' }}
              role="status"
            >
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverTracking;
